import React, { useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { WalletIcon } from '@heroicons/react/24/outline';
import BottomSheet from './BottomSheet';
import { Card, EmptyState, ListRow, SectionHeader } from './ui';
import { formatExactMoney, formatInteger, formatMoney, formatPercent } from './format';
import { typeLabel } from './periodText';
import { AGENT_TYPES, FUNDED_THRESHOLDS } from '../../utils/dashboardTree';

const SnapshotStat = ({ label, value, title, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full min-w-0 touch-manipulation flex-col rounded-xl bg-gray-50 px-3.5 py-3 text-start transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 active:bg-gray-100 dark:bg-gray-900/40 dark:hover:bg-gray-900/70 dark:active:bg-gray-900/70"
  >
    <span className="flex w-full items-center justify-between gap-1 text-xs text-gray-500 dark:text-gray-400">
      <span className="truncate">{label}</span>
      <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-gray-300 rtl:-scale-x-100 dark:text-gray-600" aria-hidden="true" />
    </span>
    <span className="mt-1 max-w-full truncate text-lg font-semibold tabular-nums text-gray-900 dark:text-white" title={title}>{value}</span>
  </button>
);

const SheetSection = ({ title, children }) => (
  <section>
    <h4 className="mb-1 text-xs font-medium text-gray-400 dark:text-gray-500">{title}</h4>
    <div className="-mx-2">{children}</div>
  </section>
);

const BalanceSheet = ({ t, network, currency }) => {
  const levels = network.byType.filter((level) => level.count > 0);
  const currencies = Object.entries(network.balances).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));

  return (
    <div className="space-y-5">
      <div>
        <p className="text-2xl font-bold tabular-nums text-gray-900 dark:text-white">{formatExactMoney(network.balances[currency] || 0, currency)}</p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('yourWalletValue', 'Your own wallet: {{value}}', { value: formatExactMoney(network.ownBalance, currency) })}
        </p>
      </div>
      <SheetSection title={t('byLevel', 'By level')}>
        {levels.map((level) => (
          <ListRow key={level.type} label={typeLabel(t, level.type, true)} aside={formatInteger(level.count)} value={formatMoney(level.total, currency)} />
        ))}
      </SheetSection>
      {currencies.length > 1 && (
        <SheetSection title={t('allCurrencies', 'All currencies')}>
          {currencies.map(([code, amount]) => (
            <ListRow key={code} label={code} value={formatExactMoney(amount, code)} />
          ))}
        </SheetSection>
      )}
    </div>
  );
};

const PlayersSheet = ({ t, network, currency }) => {
  const { players, topPlayers } = network;
  const threshold = FUNDED_THRESHOLDS[currency];
  return (
    <div className="space-y-5">
      <SheetSection title={t('playerStatus', 'Player Status')}>
        <ListRow label={t('totalPlayers', 'Players')} value={formatInteger(players.total)} />
        <ListRow label={t('newThisWeek', 'New this week')} value={formatInteger(players.newThisWeek)} />
        <ListRow
          label={t('fundedPlayers', 'Funded')}
          hint={threshold !== undefined ? t('fundedMinimum', '≥ {{amount}}', { amount: formatMoney(threshold, currency) }) : null}
          value={formatInteger(players.funded)}
        />
        <ListRow label={t('blockedPlayers', 'Blocked')} value={formatInteger(players.blocked)} />
      </SheetSection>
      <SheetSection title={t('highestBalances', 'Highest balances')}>
        {topPlayers.length === 0 ? (
          <EmptyState title={t('noBalances', 'No player has a balance in this currency')} className="!py-3" />
        ) : (
          topPlayers.map((player) => (
            <ListRow key={player.username} label={player.name} value={formatMoney(player.balance, currency)} />
          ))
        )}
      </SheetSection>
    </div>
  );
};

const AgentsSheet = ({ t, network, currency }) => {
  const { agents, topAgents } = network;
  return (
    <div className="space-y-5">
      <SheetSection title={t('hierarchy', 'Hierarchy')}>
        {AGENT_TYPES.filter((type) => agents.byType[type] > 0).map((type) => (
          <ListRow
            key={type}
            label={typeLabel(t, type, true)}
            aside={formatPercent((agents.byType[type] / agents.total) * 100, 0)}
            value={formatInteger(agents.byType[type])}
          />
        ))}
        <ListRow label={t('withPlayers', 'With players')} value={formatInteger(agents.withPlayers)} />
        <ListRow label={t('withoutPlayers', 'Without players')} value={formatInteger(agents.total - agents.withPlayers)} />
      </SheetSection>
      {topAgents.length > 0 && (
        <SheetSection title={t('mostPlayerBalance', 'Most player balance')}>
          {topAgents.map((agent) => (
            <ListRow key={agent.username} label={agent.name} value={formatMoney(agent.playerBalance, currency)} />
          ))}
        </SheetSection>
      )}
    </div>
  );
};

/** Wallets, players and agents below the viewer, right now (not tied to the selected period). */
export default function NetworkSnapshotCard({ t, currency, network, closeLabel, className = '' }) {
  const [sheet, setSheet] = useState(null);
  if (!network) return null;

  const { players, agents } = network;
  const sheets = {
    balance: { title: t('networkBalance', 'Network balance'), body: <BalanceSheet t={t} network={network} currency={currency} /> },
    players: { title: t('totalPlayers', 'Players'), body: <PlayersSheet t={t} network={network} currency={currency} /> },
    agents: { title: t('agents', 'Agents'), body: <AgentsSheet t={t} network={network} currency={currency} /> }
  };

  return (
    <Card className={className}>
      <SectionHeader icon={WalletIcon} title={t('network', 'Network')} subtitle={t('rightNow', 'Right now')} />
      <div className={`grid gap-2 sm:gap-3 ${agents.total > 0 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2'}`}>
        <div className={agents.total > 0 ? 'col-span-2 sm:col-span-1' : ''}>
          <SnapshotStat
            label={t('networkBalance', 'Network balance')}
            value={formatMoney(network.balances[currency] || 0, currency)}
            title={formatExactMoney(network.balances[currency] || 0, currency)}
            onClick={() => setSheet('balance')}
          />
        </div>
        <SnapshotStat label={t('totalPlayers', 'Players')} value={formatInteger(players.total)} onClick={() => setSheet('players')} />
        {agents.total > 0 && (
          <SnapshotStat label={t('agents', 'Agents')} value={formatInteger(agents.total)} onClick={() => setSheet('agents')} />
        )}
      </div>

      <BottomSheet
        open={Boolean(sheet)}
        onClose={() => setSheet(null)}
        title={sheet ? sheets[sheet].title : ''}
        closeLabel={closeLabel}
      >
        {sheet && sheets[sheet].body}
      </BottomSheet>
    </Card>
  );
}
